import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useRef } from "react";
import LogLayout from "../components/LogLayout";
import { MapPin, RefreshCw, Navigation, Clock, User, Signal, Crosshair } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const ABIDJAN_CENTER = [5.3600, -4.0083];

export default function LogGPSTrackingPage() {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/log/courses/gps/couriers`, { headers: getHeaders() });
      setCouriers(res.data.couriers || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 15s
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, load]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => {
      const L = window.L;
      const map = L.map(mapRef.current).setView(ABIDJAN_CENTER, 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "OpenStreetMap"
      }).addTo(map);
      mapInstanceRef.current = map;
      setTimeout(() => map.invalidateSize(), 300);
    };
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when couriers change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    const L = window.L;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    couriers.forEach(c => {
      const icon = L.divIcon({
        className: "custom-marker",
        html: `<div style="background:#3b82f6;color:white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 2L19 21l-7-4-7 4z"/></svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      const marker = L.marker([c.latitude, c.longitude], { icon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`<b>${c.courier_name}</b><br/>Precision: ${c.accuracy ? Math.round(c.accuracy) + "m" : "N/A"}<br/>Vitesse: ${c.speed ? (c.speed * 3.6).toFixed(1) + " km/h" : "N/A"}<br/><small>${new Date(c.timestamp).toLocaleTimeString("fr-FR")}</small>`);

      if (selectedCourier === c.courier_id) {
        marker.openPopup();
        mapInstanceRef.current.setView([c.latitude, c.longitude], 15);
      }
      markersRef.current.push(marker);
    });
  }, [couriers, selectedCourier]);

  const timeAgo = (ts) => {
    if (!ts) return "N/A";
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    return `${Math.floor(diff / 3600)}h`;
  };

  return (
    <LogLayout title="Suivi GPS Agents Commerciaux" subtitle="Position en temps reel des agents commerciaux actifs">
      <div data-testid="gps-tracking-page" className="space-y-4">
        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Signal className={`w-4 h-4 ${couriers.length > 0 ? "text-emerald-400" : "text-slate-500"}`} />
              <span className="text-white font-medium">{couriers.length}</span>
              <span className="text-slate-500">agent commercial{couriers.length !== 1 ? "s" : ""} actif{couriers.length !== 1 ? "s" : ""}</span>
            </div>
            <button onClick={() => setAutoRefresh(!autoRefresh)} data-testid="toggle-auto-refresh"
              className={`px-3 py-1 rounded-lg text-xs border transition-all ${autoRefresh ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "border-white/10 text-slate-500"}`}>
              {autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
            </button>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="border-white/10 text-slate-400 text-xs gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Map */}
          <div className="lg:col-span-3 bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden" style={{ minHeight: 450 }}>
            <div ref={mapRef} style={{ width: "100%", height: 450 }} data-testid="gps-map" />
          </div>

          {/* Courier list */}
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4 overflow-auto" style={{ maxHeight: 450 }}>
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><User className="w-4 h-4 text-sky-400" /> Agents Commerciaux</h3>
            {couriers.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-8">Aucun agent commercial actif dans les 30 dernieres minutes</p>
            ) : (
              <div className="space-y-2">
                {couriers.map(c => (
                  <button key={c.courier_id} onClick={() => setSelectedCourier(c.courier_id)} data-testid={`courier-${c.courier_id}`}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${selectedCourier === c.courier_id ? "bg-sky-500/10 border-sky-500/20" : "border-white/5 hover:bg-white/5"}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/100/20 flex items-center justify-center">
                        <Navigation className="w-4 h-4 text-blue-400" style={{ transform: c.heading ? `rotate(${c.heading}deg)` : "none" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{c.courier_name}</p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          <Clock className="w-3 h-3" /> {timeAgo(c.timestamp)}
                          {c.speed && <span className="text-sky-400">{(c.speed * 3.6).toFixed(0)} km/h</span>}
                        </div>
                      </div>
                      <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </LogLayout>
  );
}
